import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, IconButton, Tag } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import {
  CommissionPolicy,
  CommissionType,
  CommissionTypeList,
  SellableFeatureList,
} from '@/models/marketplace.model';
import { getCommissionPolicies } from '@/services/marketplace.service';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import PageContent from '@/components/PageContent';
import CommissionPolicyDrawer from './commission-policy-drawer';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const CommissionPolicyList = () => {
  const [items, setItems] = useState<CommissionPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CommissionPolicy | undefined>(undefined);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result: BaseResponseModel<CommissionPolicy[]> = await getCommissionPolicies();
      if (result.success) {
        setItems(result.result);
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching commission policies: ${errorModel?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditing(undefined);
    setOpenDrawer(true);
  };

  const handleEdit = (policy: CommissionPolicy) => {
    setEditing(policy);
    setOpenDrawer(true);
  };

  const handleSubmitted = () => {
    setOpenDrawer(false);
    setEditing(undefined);
    fetchData();
  };

  const formatValue = (policy: CommissionPolicy) =>
    policy.type === CommissionType.Percentage ? `${policy.value}%` : `${policy.value} pts`;

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={handleAdd}>
          Add Policy
        </Button>
      </Stack>

      <Table height={Math.max(getHeight(window) - 350, 400)} data={items} loading={loading}>
        <Column flexGrow={2} align="left">
          <HeaderCell>Feature</HeaderCell>
          <Cell>
            {(rowData: CommissionPolicy) =>
              SellableFeatureList.find(f => f.value === rowData.feature)?.label ?? rowData.feature
            }
          </Cell>
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Type</HeaderCell>
          <Cell>
            {(rowData: CommissionPolicy) => (
              <Tag color={rowData.type === CommissionType.Percentage ? 'violet' : 'blue'}>
                {CommissionTypeList.find(t => t.value === rowData.type)?.label ?? rowData.type}
              </Tag>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} align="right">
          <HeaderCell>Commission</HeaderCell>
          <Cell>{(rowData: CommissionPolicy) => formatValue(rowData)}</Cell>
        </Column>
        <Column flexGrow={1} align="right">
          <HeaderCell>Minimum Price</HeaderCell>
          <Cell>{(rowData: CommissionPolicy) => `${rowData.minimumPrice} pts`}</Cell>
        </Column>
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: CommissionPolicy) => (
              <IconButton appearance="subtle" icon={<EditIcon />} onClick={() => handleEdit(rowData)} />
            )}
          </Cell>
        </Column>
      </Table>

      <CommissionPolicyDrawer
        open={openDrawer}
        policy={editing}
        onClose={() => setOpenDrawer(false)}
        onSubmitted={handleSubmitted}
      />
    </PageContent>
  );
};

export default CommissionPolicyList;
